import { useMemo, useState } from 'react'
import { byId } from '../data/catalog'
import { DISCOVERY_MARKS_PER_TICKET, feedNeeded, fishStage } from '../domain/economy'
import { useAppStore } from '../store'
import type { View } from '../types'
import { Aquarium } from '../components/Aquarium'
import { DiscoveryModal } from '../components/DiscoveryModal'
import { Icon } from '../components/Icons'

export function CoveView({ navigate }: { navigate: (view: View) => void }) {
  const { state, feedFish } = useAppStore()
  const [discovering, setDiscovering] = useState(false)
  const [selectedFishId, setSelectedFishId] = useState(state.fish[0]?.id)
  const selectedFish = state.fish.find((fish) => fish.id === selectedFishId) ?? state.fish[0]
  const fishItem = selectedFish ? byId.get(selectedFish.catalogueId) : undefined
  const lastCompleted = useMemo(() => state.sessions.find((session) => session.status === 'completed'), [state.sessions])

  return (
    <main className="view cove-view">
      <section className="cove-hero">
        <Aquarium />
        <div className="cove-topbar">
          <div><p className="eyebrow">Your living focus space</p><h1>Good to see you.</h1></div>
          <div className="resource-row">
            <span className="resource-pill"><Icon name="feed" /> {state.feed}</span>
            <button className="resource-pill button-pill" onClick={() => setDiscovering(true)}><Icon name="ticket" /> {state.tickets}</button>
          </div>
        </div>
        <div className="quick-launch glass-panel">
          <div>
            <span className="label">Ready when you are</span>
            <strong>{lastCompleted?.title ?? 'Choose one clear task'}</strong>
          </div>
          <button className="primary round" onClick={() => navigate('focus')} aria-label="Start a focus session"><Icon name="play" /></button>
        </div>
      </section>

      <section className="dashboard-grid">
        <article className="panel growth-panel">
          <div className="panel-heading"><div><p className="eyebrow">Gentle growth</p><h2>{fishItem?.name ?? 'Your starter fish'}</h2></div><Icon name="feed" /></div>
          {selectedFish && fishItem && (
            <>
              <div className="fish-switcher">
                {state.fish.map((fish) => {
                  const item = byId.get(fish.catalogueId)
                  return <button key={fish.id} className={fish.id === selectedFish.id ? 'active' : ''} onClick={() => setSelectedFishId(fish.id)}>{item?.name.split(' ').at(-1)?.slice(0, 2)}</button>
                })}
              </div>
              <div className="growth-copy"><span>Stage {fishStage(selectedFish.feedTotal)} of 3</span><span>{feedNeeded(selectedFish.feedTotal) ? `${feedNeeded(selectedFish.feedTotal)} feed to grow` : 'Fully grown'}</span></div>
              <div className="progress-track"><i style={{ width: `${Math.min(100, selectedFish.feedTotal < 20 ? (selectedFish.feedTotal / 20) * 100 : 50 + ((selectedFish.feedTotal - 20) / 50) * 50)}%` }} /></div>
              <button className="secondary wide" disabled={!state.feed || feedNeeded(selectedFish.feedTotal) === 0} onClick={() => feedFish(selectedFish.id)}>
                <Icon name="feed" /> {state.feed ? 'Feed toward next stage' : 'Complete a session to earn feed'}
              </button>
            </>
          )}
        </article>

        <article className="panel discovery-panel">
          <div className="panel-heading"><div><p className="eyebrow">Next discovery</p><h2>{state.discoveryMarks}/{DISCOVERY_MARKS_PER_TICKET} shell marks</h2></div><Icon name="sparkle" /></div>
          <div className="shell-progress" aria-label={`${state.discoveryMarks} of ${DISCOVERY_MARKS_PER_TICKET} Discovery Marks`}>
            {Array.from({ length: DISCOVERY_MARKS_PER_TICKET }, (_, index) => <i key={index} className={index < state.discoveryMarks ? 'filled' : ''} />)}
          </div>
          <p>{state.tickets ? 'A focus-earned discovery is waiting for you.' : `${DISCOVERY_MARKS_PER_TICKET - state.discoveryMarks} more marks until your next ticket.`}</p>
          <button className="secondary wide" disabled={!state.tickets} onClick={() => setDiscovering(true)}><Icon name="ticket" /> Open Discovery Shell</button>
        </article>
      </section>
      {discovering && <DiscoveryModal onClose={() => setDiscovering(false)} />}
    </main>
  )
}
